import { Button, Card, Grid, IconButton, TextField, Tooltip, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { Download, GetApp, Close, KeyboardArrowDown } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/DataTable";
import CustomModal from "components/CustomizedMui/CustomModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import moment from "moment";
import fetchWeeklyActivityReport from "api/jobs/fetchWeeklyActivityReport";
import exportWeeklyActivityReport from "api/jobs/exportWeeklyActivityReport";
import usePreferredPageSize from "hooks/usePreferredPageSize";

const useStyles = makeStyles({
  dateField: {
    marginBottom: "16px",
    "& .MuiInputBase-root": {
      height: "56px",
    },
  },
});

const WeeklyActivityReportModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const { limit, setLimit, pageLimitConfirmationModal } = usePreferredPageSize(10);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("newPositionDate");
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [dateRange, setDateRange] = useState("Last 7 days");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
    dateRange: "Last 7 days",
  });

  // Process date range
  const getDateRange = (dateRangeValue, startDateValue, endDateValue) => {
    const today = moment().endOf("day");

    switch (dateRangeValue) {
      case "Year to date":
        return {
          startDate: moment().startOf("year").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
      case "Month to date":
        return {
          startDate: moment().startOf("month").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
      case "Last 12 months":
        return {
          startDate: moment().subtract(12, "months").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
      case "Last 30 days":
        return {
          startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
      case "Last 7 days":
        return {
          startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
      case "Custom":
        return {
          startDate: startDateValue || undefined,
          endDate: endDateValue || undefined,
        };
      default:
        return {
          startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
        };
    }
  };

  // Get processed dates from applied filters
  const { startDate: processedStartDate, endDate: processedEndDate } = getDateRange(
    appliedFilters.dateRange,
    appliedFilters.startDate,
    appliedFilters.endDate
  );

  const queryParams = {
    page,
    limit,
    startDate: processedStartDate,
    endDate: processedEndDate,
  };

  // Initialize applied filters on mount
  useEffect(() => {
    if (open) {
      const initialFilters = {
        startDate: "",
        endDate: "",
        dateRange: "Last 7 days",
      };
      setAppliedFilters(initialFilters);
      setStartDate("");
      setEndDate("");
      setDateRange("Last 7 days");
      setPage(1);
    }
  }, [open]);

  const {
    data: weeklyActivityData,
    isLoading,
    refetch,
  } = useQuery(
    ["weekly-activity-report", page, limit, processedStartDate, processedEndDate],
    () => fetchWeeklyActivityReport(queryParams),
    {
      enabled: Boolean(open && processedStartDate && processedEndDate),
      refetchOnWindowFocus: false,
    }
  );

  const exportMutation = useMutation(exportWeeklyActivityReport, {
    onSuccess: () => {
      enqueueSnackbar("Weekly activity report exported successfully!", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Export failed: ${error.message}`, { variant: "error" });
    },
  });

  const handleExport = useCallback(
    (format = "csv") => {
      exportMutation.mutate({
        startDate: processedStartDate,
        endDate: processedEndDate,
        format,
      });
    },
    [exportMutation, processedStartDate, processedEndDate]
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      startDate,
      endDate,
      dateRange,
    });
    setPage(1);
  }, [startDate, endDate, dateRange]);

  // Ensure current page doesn't exceed total pages
  useEffect(() => {
    if (
      weeklyActivityData?.pagination?.totalPages &&
      page > weeklyActivityData.pagination.totalPages
    ) {
      setPage(1);
    }
  }, [weeklyActivityData?.pagination?.totalPages, page]);

  const handleClearFilters = useCallback(() => {
    setStartDate("");
    setEndDate("");
    setDateRange("Last 7 days");
    setAppliedFilters({
      startDate: "",
      endDate: "",
      dateRange: "Last 7 days",
    });
    setPage(1);
  }, []);

  const handleExportClick = useCallback((event) => {
    setExportAnchorEl(event.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportAnchorEl(null);
  }, []);

  const handleExportOption = useCallback(
    (format) => {
      handleExport(format);
      handleExportClose();
    },
    [handleExport, handleExportClose]
  );

  const columns = useMemo(
    () => [
      {
        title: "Account Manager",
        field: "accountManager",
        width: "12%",
      },
      {
        title: "Customer Name",
        field: "customerName",
        width: "12%",
      },
      {
        title: "Client ID",
        field: "clientId",
        width: "10%",
      },
      {
        title: "Job Title",
        field: "jobTitle",
        width: "15%",
      },
      {
        title: "Fill Date",
        field: "fillDate",
        width: "10%",
        customCell: (date) => {
          if (!date || date === "N/A") return "N/A";
          const momentDate = moment(date);
          return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "N/A";
        },
      },
      {
        title: "Filled By Name",
        field: "filledByName",
        width: "12%",
      },
      {
        title: "Applicant Name",
        field: "applicantName",
        width: "12%",
      },
      {
        title: "Interview Event Date",
        field: "interviewEventDate",
        width: "12%",
        customCell: (date) => {
          if (!date || date === "N/A") return "N/A";
          const momentDate = moment(date);
          return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "N/A";
        },
      },
      {
        title: "New Position Date",
        field: "newPositionDate",
        width: "12%",
        customCell: (date) => {
          if (!date || date === "N/A") return "N/A";
          const momentDate = moment(date);
          return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "N/A";
        },
      },
      {
        title: "Modified Date",
        field: "modifiedDate",
        width: "12%",
        customCell: (date) => {
          if (!date || date === "N/A") return "N/A";
          const momentDate = moment(date);
          return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "N/A";
        },
      },
    ],
    []
  );

  const toggleSort = useCallback(
    (field) => {
      if (orderBy === field) {
        setOrder(order === "asc" ? "desc" : "asc");
      } else {
        setOrderBy(field);
        setOrder("asc");
      }
    },
    [order, orderBy]
  );

  return (
    <CustomModal
      isOpen={open}
      onClose={onClose}
      cardSx={{ maxWidth: "85%", maxHeight: "90%", overflowY: "scroll", padding: 2 }}
    >
      <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDTypography variant="h6">Weekly Activity Report</MDTypography>
          <MDBox display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExportClick}
              disabled={exportMutation.isLoading}
              endIcon={<KeyboardArrowDown />}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={() => handleExportOption("csv")}>
                <Download sx={{ mr: 1 }} />
                Export as CSV
              </MenuItem>
              <MenuItem onClick={() => handleExportOption("xlsx")}>
                <GetApp sx={{ mr: 1 }} />
                Export as Excel
              </MenuItem>
            </Menu>
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Date Range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                fullWidth
                className={classes.dateField}
              >
                <MenuItem value="Last 7 days">Last 7 days</MenuItem>
                <MenuItem value="Last 30 days">Last 30 days</MenuItem>
                <MenuItem value="Last 12 months">Last 12 months</MenuItem>
                <MenuItem value="Month to date">Month to date</MenuItem>
                <MenuItem value="Year to date">Year to date</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </TextField>
            </Grid>

            {dateRange === "Custom" && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <>
                  <Grid item xs={12} sm={3}>
                    <DatePicker
                      label="Start Date"
                      value={startDate ? moment(startDate) : null}
                      onChange={(newValue) =>
                        setStartDate(newValue ? newValue.format("YYYY-MM-DD") : "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth className={classes.dateField} />
                      )}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <DatePicker
                      label="End Date"
                      value={endDate ? moment(endDate) : null}
                      onChange={(newValue) =>
                        setEndDate(newValue ? newValue.format("YYYY-MM-DD") : "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth className={classes.dateField} />
                      )}
                      slotProps={{
                        textField: {
                          variant: "outlined",
                        },
                      }}
                    />
                  </Grid>
                </>
              </LocalizationProvider>
            )}

            <Grid item xs={12} sm={dateRange === "Custom" ? 2 : 8}>
              <MDBox display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  disabled={isLoading}
                >
                  {isLoading ? "Applying..." : "Apply Filters"}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClearFilters}
                  disabled={isLoading}
                >
                  Clear Filters
                </Button>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox sx={{ flex: 1, overflow: "hidden" }}>
          <DataTable
            columns={columns}
            data={{
              data: weeklyActivityData?.data || [],
              count: weeklyActivityData?.pagination?.total || 0,
            }}
            page={page}
            limit={limit}
            setPage={setPage}
            setLimit={setLimit}
            isLoading={isLoading}
            order={order}
            orderBy={orderBy}
            toggleSort={toggleSort}
            fetchAll={false}
            defaultSort="newPositionDate"
            idField="newPositionDate"
          />
        </MDBox>
      </MDBox>
    </CustomModal>
  );
};

export default WeeklyActivityReportModal;
